import Vue from 'Vue';
import _ from 'lodash';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'vue-loading-overlay/dist/vue-loading.css';

import dcUtilities from '../../utilities/dc.utility';
import geojsonUtilities from '../../utilities/geojson.utility';
import mapType from '../../types/mapType';

import Popup from '../dc-popup/dc-popup';

import {instance as dataService} from '../../services/data.service';
import layerFactory from '../../utilities/factories/mapboxgl.layer.factory';
import popupFactory from '../../utilities/factories/mapboxgl.popup.factory';

/**
 * ********************************************************************
 * standard import lines for every component. Importing the template
 * and the component specific style.
 * ********************************************************************
 */
import './dc-map.style.scss';
import template from './dc-map.template.htm';
import settings from "../../settings/settings";

/**
 * *********************************************************************
 */

export default Vue.component('dc-map', {
    template: template,
    props: {
        locale: {
            type: String,
            default: 'de'
        },
        appId: {
            type: String,
            default: 'dataCycle-widget'
        },
        mapWidth: {
            type: String,
            default: '100%'
        },
        mapHeight: {
            type: String,
            default: '600px'
        },
        mapZoom: {
            type: Number,
            default: 5
        },
        mapLon: {
            type: Number,
            default: 12.2
        },
        mapLat: {
            type: Number,
            default: 48.4
        },
        markerHeight: {
            type: Number,
            default: 44
        },
        mapType: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            mouseZoomTimeout: null,
            tooltip: new Popup({
                parent: this
            }),
            popup: new Popup({
                parent: this
            }),
            map: {}
        };
    },
    computed: {
        appId() {
            return this.$store.getters.appId;
        },
        initialFeaturesList(){
            return this.$store.getters.initialFeaturesList;
        },
        contents() {
            return this.$store.getters.contents;
        },
        contentsData() {
            return [...this.initialFeaturesList, ...this.contents.data];
        },
        isLoading: {
            get() {
                return this.$store.getters.isLoading;
            },
            set(newValue) {
                this.$store.commit('setIsLoading', newValue);
            }
        },
        mapStyle() {
            return this.$store.getters.mapStyle;
        },
        activeTrip() {
            return this.$store.getters.activeTrip;
        }
    },
    watch: {
        activeTrip(newTrip, oldTrip) {
            this.onMapIdle().then(() => {
                let tripId = _.get(newTrip, 'id', '');
                let oldTripId = _.get(oldTrip, 'id', '');

                // first the hover effect
                let stages = _.get(newTrip, 'original.details[0].tour[0].stages', []);
                let pois = _.get(newTrip, 'original.details[0].tour[0].poi',[]);
                let stageIds = stages.map(stage => stage['@id']);
                let poiIds = pois.map(poi => poi['@id']);
                let oldStages = _.get(oldTrip, 'original.details[0].tour[0].stages', []);
                let oldPois = _.get(oldTrip, 'original.details[0].tour[0].poi',[]);
                let oldStageIds = oldStages.map(stage => stage['@id']);
                let oldPoiIds = oldPois.map(poi => poi['@id']);
                if (!!oldTripId) {
                    layerFactory.setFeatureState(oldTripId, [...oldStageIds, ...oldPoiIds], {hover: false});
                    layerFactory.setLayoutProperty(oldTripId, `${oldTripId}.pois`, 'icon-image', 'default');
                    layerFactory.setPaintProperty(oldTripId, `${oldTripId}.tours`, 'line-color', settings.colors.default);
                    layerFactory.setPaintProperty(oldTripId, `${oldTripId}.tourcases`, 'line-opacity', 0);
                    layerFactory.setPaintProperty(oldTripId, `${oldTripId}.blacktourcases`, 'line-opacity', 0);
                }
                if (!!tripId) {
                    layerFactory.setFeatureState(tripId, [...stageIds, ...poiIds], {hover: true});
                    this.highlightTour(tripId);
                }
                    //second we fly to the new bounds
                let bounds = layerFactory.getBounds(tripId);

                if (!!bounds && !!bounds.isEmpty && !bounds.isEmpty()) {
                    this.map.fitBounds(bounds, {
                        padding: dcUtilities.getMapPadding(this.appId)
                    });
                }
                else {
                    //const detailPageUrl = dcUtilities.getDetailpageUrl(tripId);
                    //window.open(detailPageUrl, "_blank");
                }
            });
        },
        contentsData: {
            deep: true,
            handler(newData, oldData) {
                this.onMapIdle().then(() => {

                    this.contentsData.map(trip => {

                        const tour = _.get(trip, 'details[0].tour[0]', null);

                        if (tour != null) {

                            const tripId = _.get(trip, '@id', '');
                            if (!layerFactory.hasLayerAndSourceObject(tripId)) {
                                const imageUrl = _.get(trip, 'details[0].image[0].thumbnailUrl', '');
                                const stages = _.get(trip, 'details[0].tour[0].stages', []);
                                const pois = _.get(trip, 'details[0].tour[0].poi', []);

                                const tourName = _.get(trip, 'details[0].tour[0].name', '');
                                const dataToTransform = _.flatten([
                                    stages.map(stage => {
                                        let linestring = _.get(stage, 'geo.line', '');
                                        let pointsArray = geojsonUtilities.parseGeojsonString(linestring);

                                        var linetype = 'line';
                                        if(linestring.indexOf("MULTILINESTRING") !== -1){
                                            linetype = 'multiline';
                                        }
                                        else if(linestring.indexOf("LINESTRING") !== -1){
                                            linetype = 'line';
                                        }

                                        /****************************************************************************
                                        let matches = linestring.match("LINESTRING Z [\(]+([^)]+)[\)]+");
                                        let points = matches[1];

                                        // ------------------ remove first char if not a string ----------------------
                                            let firstChar = points.charAt(0);
                                            if(isNaN(parseInt(firstChar))){
                                                points = points.substr(1);
                                            }
                                        // ---------------------------------------------------------------------------

                                        let pointsArray = points.split(",").map(group => {
                                            return group.trim().split(" ").slice(0, 2).map(c => parseFloat(c));
                                        });
                                        //****************************************************************************/

                                        let result = {
                                            //line: pointsArray,//_.get(stage, 'geo.line',''),
                                            id: _.get(stage, '@id', ''),
                                            name: _.get(stage, 'name', ''),
                                            tourName: tourName,
                                            type:'tour',
                                            image: imageUrl
                                        };
                                        result[linetype] = pointsArray;

                                        return result;
                                    }),
                                    pois.map(poi => {
                                        return {
                                            point: {
                                                long: _.get(poi, 'geo.longitude', 0.0),
                                                lat: _.get(poi, 'geo.latitude', 0.0)
                                            },
                                            id: _.get(poi, '@id', ''),
                                            image: _.get(poi, 'image[0].thumbnailUrl', settings.defaults.thumbnail),//imageUrl,
                                            name: _.get(poi, 'name', ''),
                                            type:'poi',
                                            description: _.get(poi, 'description', '')
                                        };
                                    })
                                ]);
                                const geojsonData = geojsonUtilities.toGeoJSON(dataToTransform);
                                this.addSourceAndLayer(tripId, geojsonData);
                            }
                            else {
                                layerFactory.show(tripId);
                            }
                        }
                    });

                    let tripIds = this.contentsData.map(trip => {
                        return _.get(trip, '@id', '');
                    }).filter(trip => trip);

                    if (!!oldData) {
                        oldData.map(oldTrip => {
                            let tripId = _.get(oldTrip, '@id', '');
                            if (!tripIds.includes(tripId)) {
                                if (layerFactory.hasLayerAndSourceObject(tripId)) {
                                    layerFactory.hide(tripId);
                                }
                            }
                        });
                    }
                    this.onMapIdle().then(() => {
                        this.contentsData.map(trip => {
                            const tripId = _.get(trip, '@id', '');
                            layerFactory.layerToFront(`${tripId}.pois`);
                        });
                    });
                });
            }
        }
    },
    created() {

    },
    mounted() {
        this.mapStyle.height = this.mapHeight;
        this.mapStyle.width = this.mapWidth;

        this.setupMap();
    },
    methods: {
        onMapIdle() {
            return new Promise((resolve, reject) => {
                let isIdle = this.$store.getters.mapIdle;
                if (isIdle) {
                    resolve();
                }
                else {
                    this.map.once('idle', () => {
                        resolve();
                    })
                }
            });
        },
        highlightTour(tripId){
            layerFactory.setLayoutProperty(tripId, `${tripId}.pois`, 'icon-image', 'active');
            layerFactory.setPaintProperty(tripId, `${tripId}.tours`, 'line-color', settings.colors.active);
            layerFactory.setPaintProperty(tripId, `${tripId}.tourcases`, 'line-opacity', 1);
            layerFactory.setPaintProperty(tripId, `${tripId}.blacktourcases`, 'line-opacity', 1);
            layerFactory.layerToFront(`${tripId}.blacktourcases`);
            layerFactory.layerToFront(`${tripId}.tourcases`);
            layerFactory.layerToFront(`${tripId}.tours`);
            layerFactory.layerToFront(`${tripId}.pois`);
        },
        loadGeoData() { //@deprecated
            return new Promise((resolve, reject) => {
                dataService.load()
                    .then(result => {
                        this.contents.data = result.data;
                        this.contents.meta = result.meta;
                        this.contents.page++;
                        resolve();
                    })
                    .catch(error => {
                        if (error.response) {
                            alert(error.response.status + ' ' + error.response.statusText + '\n' + error.response.data.error);
                        } else if (error.request) {
                            console.log(error.request);
                        } else {
                            console.log('Error', error.message);
                        }
                        console.log(error.config);
                        this.isLoading = false;
                        reject();
                    });
            });
        },
        setupMap() {
            // TODO: make images a config parameter
            var imagesLoaded = [];
            var imageUrls = {
                clustered: `${settings.assetPath}/img/cluster_default.png`,
                default: `${settings.assetPath}/img/cluster_default_blank.png`,
                active: `${settings.assetPath}/img/cluster_default_active.png`
            };

            mapboxgl.accessToken = "pk.eyJ1IjoiZG9uYXV0b3VyaXN0aWsiLCJhIjoiY2tjYTlvaHBxMWNmNDJ5cDhybXNrbzdsZyJ9.nklGuDQWiEcdvjJln05z-Q";//process.env.VUE_APP_MAPBOX_ACCESS_TOKEN;

            const isInteractive = this.mapType != mapType.simple;
            console.log(`is this interactive? ${this.mapType} != ${mapType.simple}`);

            this.map = new mapboxgl.Map({
                container: 'datacycle-map-widget',
                style: 'mapbox://styles/mapbox/outdoors-v11',
                center: [this.mapLon, this.mapLat],
                zoom: this.mapZoom,
                locale: this.locale,
                interactive: isInteractive
            });

            Window.dcM = this.map;

            this.map.on('idle', () => {
                geojsonUtilities.translate(this.map, this.locale);
                this.$store.commit('setMapIdle', true);
            });
            this.map.on('dataloading', () => {
                this.$store.commit('setMapIdle', false);
            });

            for (const key in imageUrls) {
                if (imageUrls.hasOwnProperty(key)) {
                    const value = imageUrls[key];
                    imagesLoaded.push(dcUtilities.loadImage(this.map, key, value));
                }
            }

            Promise.all(imagesLoaded).then(() => {

                layerFactory.initialise(this.map, this);
                popupFactory.initialise(this.map, this);

                this.addListeners();

                // resize to remove unwanted borders
                this.map.resize();
            });
        },
        addSourceAndLayer(id, geojsonData) {
            layerFactory.addSource(id, {
                type: 'geojson',
                data: geojsonData,
                cluster: false,
                clusterMaxZoom: 14,
                clusterRadius: 50
            });
            layerFactory.onSourceReady(id).done(() => {
                layerFactory.addLayer(`${id}.tours`, {
                    type: 'line',
                    'layout': {
                        'line-join': 'round',
                        'line-cap': 'round'
                    },
                    'filter':['==',['get','type'],'tour'],
                    'paint': {
                        'line-color': settings.colors.default,
                        'line-width': 2
                    }
                });
                layerFactory.addLayer(`${id}.tourcases`, {
                    type: 'line',
                    'layout': {
                        'line-join': 'round',
                        'line-cap': 'round'
                    },
                    'filter':['==',['get','type'],'tour'],
                    'paint': {
                        'line-color': settings.colors.white,
                        'line-width': 4,
                        'line-opacity': 0
                    }
                });
                layerFactory.addLayer(`${id}.blacktourcases`, {
                    type: 'line',
                    'layout': {
                        'line-join': 'round',
                        'line-cap': 'round'
                    },
                    'filter':['==',['get','type'],'tour'],
                    'paint': {
                        'line-color': settings.colors.black,
                        'line-width': 6,
                        'line-opacity': 0
                    }
                });
                layerFactory.addLayer(`${id}.pois`, {
                    type: 'symbol',
                    'filter':['==',['get','type'],'poi'],
                    layout: {
                        'icon-image': 'default',
                        'icon-anchor': 'bottom'
                    }
                });
                this.setupLayerEvents(id);

                this.map.once('idle', () => {
                    if(!!this.activeTrip){
                        this.highlightTour(this.activeTrip.id);
                    }
                })
            });
        },
        setupLayerEvents(id) {
            layerFactory.on('mouseover', `${id}.tours`, e => {
                console.log("tour hover");
            });
            layerFactory.on('mouseover', `${id}.pois`, e => {
                console.log("poi hover");
                var feature = e.features && e.features.length > 0 ? e.features[0] : null;
                console.log(feature);
                this.tooltip.item = feature.properties;
                let element = this.tooltip.$mount().$el;

                let coordinates = JSON.parse(feature.properties.point);
                coordinates = [coordinates.long, coordinates.lat];

                popupFactory.show({
                    id: 'poiTooltip',
                    className: `poi-popup`,
                    coordinates: coordinates,
                    content: element,
                    closeOnClick: true
                });
                this.map.getCanvas().style.cursor = 'pointer';
            });
            layerFactory.on('mouseout', `${id}.pois`, e => {
                this.map.getCanvas().style.cursor = '';
                popupFactory.hide('poiTooltip');
            });
            layerFactory.on('click', `${id}.pois`, e => {
                console.log("poi click");
                var feature = e.features && e.features.length > 0 ? e.features[0] : null;
                let properties = {
                    ...feature.properties,
                    link: dcUtilities.getPoiDetailpageUrl(feature.properties.id, this.locale),
                    isPermanent: true
                };
                this.popup.item = properties;
                let element = this.popup.$mount().$el;

                let coordinates = JSON.parse(feature.properties.point);
                coordinates = [coordinates.long, coordinates.lat];

                popupFactory.show({
                    id: 'poiPopup',
                    className: `poi-popup`,
                    coordinates: coordinates,
                    content: element,
                    closeOnClick: true
                });
            });
        },

        addListeners() {
            // inspect a cluster on click
            this.map.on('click', 'clusters', e => {
                var features = this.map.queryRenderedFeatures(e.point, {
                    layers: ['clusters']
                });
                var clusterId = features[0].properties.cluster_id;
                this.map.getSource('pois').getClusterExpansionZoom(clusterId, (err, zoom) => {
                    if (err) return;

                    this.map.easeTo({
                        center: features[0].geometry.coordinates,
                        zoom: zoom
                    });
                });
            });

            this.map.on('mouseenter', 'clusters', () => {
                this.map.getCanvas().style.cursor = 'pointer';
            });
            this.map.on('mouseleave', 'clusters', () => {
                this.map.getCanvas().style.cursor = '';
            });

            //show Popup for points
            this.map.on('click', 'points', async e => {
                var coordinates = e.features[0].geometry.coordinates.slice();
                var content = await this.createPopupContent(e.features[0].properties);

                // Ensure that if the map is zoomed out such that multiple
                // copies of the feature are visible, the popup appears
                // over the copy being pointed to.
                while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                    coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                }

                new mapboxgl.Popup({offset: [0, -this.markerHeight]})
                    .setLngLat(coordinates)
                    .setHTML(content)
                    .addTo(this.map);
            });

            // Change the cursor to a pointer when the mouse is over the points layer.
            this.map.on('mouseenter', 'points', () => {
                this.map.getCanvas().style.cursor = 'pointer';
            });

            // Change it back to a pointer when it leaves.
            this.map.on('mouseleave', 'points', () => {
                this.map.getCanvas().style.cursor = '';
            });

            // Prevent scroll-wheel-zooming without key
            // TODO: mobile
            if(this.mapType != mapType.simple) {
                this.map.on('wheel', event => {
                    let scrollOverlay = document.getElementsByClassName('scroll-overlay')[0];

                    if (!scrollOverlay) {
                        let container = document.getElementsByClassName('mapboxgl-canvas-container')[0];

                        scrollOverlay = document.createElement('div');
                        scrollOverlay.classList.add('scroll-overlay');

                        let scrollText = document.createElement('div');
                        scrollText.classList.add('scroll-overlay-text');
                        scrollText.innerHTML = this.$t('scroll_on_map');

                        scrollOverlay.appendChild(scrollText);
                        container.prepend(scrollOverlay);
                    }

                    if (event.originalEvent.ctrlKey) {
                        scrollOverlay.classList.remove('scroll-overlay-visible');
                        return;
                    }

                    if (event.originalEvent.metaKey) {
                        scrollOverlay.classList.remove('scroll-overlay-visible');
                        return;
                    }

                    if (event.originalEvent.altKey) {
                        scrollOverlay.classList.remove('scroll-overlay-visible');
                        return;
                    }

                    scrollOverlay.classList.add('scroll-overlay-visible');

                    window.clearTimeout(this.mouseZoomTimeout);
                    this.mouseZoomTimeout = window.setTimeout(() => {
                        scrollOverlay.classList.remove('scroll-overlay-visible');
                    }, 1000);

                    event.preventDefault();
                });
            }
        },
    }
});